import React, { useState, useEffect } from 'react';
import { Settings, Save, CheckCircle, AlertCircle, ExternalLink, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const validateN8nUrl = (value, appHostname = window.location.hostname) => {
  let parsed;
  try {
    parsed = new URL(String(value || '').trim());
  } catch {
    return 'Enter a complete n8n URL.';
  }
  if (!['http:', 'https:'].includes(parsed.protocol)) return 'Use an HTTP or HTTPS URL.';
  const appIsLocal = ['localhost', '127.0.0.1', '::1'].includes(appHostname);
  if (!appIsLocal && ['localhost', '127.0.0.1', '::1'].includes(parsed.hostname)) {
    return 'A hosted app cannot connect to localhost on your device. Enter the hosted n8n URL.';
  }
  return null;
};

const N8NConfiguration = () => {
  const [config, setConfig] = useState({
    n8n_url: process.env.REACT_APP_N8N_URL || '',
    api_key: '',
    webhook_secret: '',
    is_active: true
  });
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testing, setTesting] = useState(false);
  const [savedIntegrationId, setSavedIntegrationId] = useState(null);
  const [savedN8nUrl, setSavedN8nUrl] = useState('');

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');

  useEffect(() => {
    fetchConfiguration();
    fetchWorkflows();
  }, []);

  const fetchConfiguration = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get n8n config from integrations collection
      const res = await fetch(`${API_URL}/api/integrations?integration_type=n8n`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          const n8nConfig = data.find(c => c.provider === 'n8n') || data[0];
          const savedUrl = n8nConfig.config?.n8n_url || '';
          setSavedIntegrationId(n8nConfig.id || null);
          setSavedN8nUrl(savedUrl);
          setConfig({
            n8n_url: savedUrl,
            api_key: '',
            webhook_secret: '',
            is_active: n8nConfig.is_active !== false
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch n8n configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const res = await fetch(`${API_URL}/api/n8n/workflows`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || ''
        }
      });
      if (res.ok) {
        const data = await res.json();
        setWorkflows(data.workflows || []);
      }
    } catch (err) {
      console.error('Failed to fetch workflows:', err);
    }
  };

  const saveConfiguration = async () => {
    const urlError = validateN8nUrl(config.n8n_url);
    if (urlError) {
      setError(urlError);
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Save to integrations collection
      const integrationData = {
        integration_type: 'n8n',
        provider: 'n8n',
        is_active: config.is_active,
        config: {
          n8n_url: config.n8n_url
        },
        credentials: Object.fromEntries(
          Object.entries({ api_key: config.api_key, webhook_secret: config.webhook_secret })
            .filter(([, value]) => String(value || '').trim()),
        ),
      };

      const res = await fetch(`${API_URL}/api/integrations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(integrationData)
      });

      if (res.ok) {
        const data = await res.json();
        setSavedIntegrationId(data.integration_id);
        setSavedN8nUrl(config.n8n_url.trim());
        setSuccess('n8n configuration saved successfully');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to save configuration');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    if (!savedIntegrationId || config.n8n_url.trim() !== savedN8nUrl) {
      setError('Save the current n8n URL before testing the connection.');
      return;
    }
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      // Use backend API to test connection (avoids CORS issues)
      const res = await fetch(`${API_URL}/api/integrations/${encodeURIComponent(savedIntegrationId)}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSuccess('Connection to n8n successful!');
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError(data.message || 'n8n is not responding. Check if n8n is running and URL is correct.');
        }
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || 'The server could not reach n8n. Check the configured URL and integration status.');
      }
    } catch (err) {
      setError(`Connection test failed: ${err.message}`);
    } finally {
      setTesting(false);
    }
  };

  const urlError = config.n8n_url ? validateN8nUrl(config.n8n_url) : null;
  const canOpenN8n = Boolean(config.n8n_url && !urlError);
  const webhookBase = `${API_URL || window.location.origin}/api/n8n/webhook/`;

  if (loading && !config.n8n_url) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">n8n (RestoFlow) Configuration</h1>
        <p className="text-gray-600">Configure your automation hub for workflow orchestration</p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center space-x-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded flex items-center space-x-2">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Connection Settings</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">n8n URL</label>
            <input aria-label="n8n URL"
              type="url"
              value={config.n8n_url}
              onChange={(e) => setConfig({ ...config, n8n_url: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="https://automation.example.com"
            />
            <p className="text-xs text-gray-500 mt-1">Use the URL your browser can reach. Leave credential fields blank to keep saved keys.</p>
            {urlError && <p role="alert" className="text-xs text-red-700 mt-1">{urlError}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">API Key (Optional)</label>
            <input aria-label="API Key (Optional)"
              type="password"
              value={config.api_key}
              onChange={(e) => setConfig({ ...config, api_key: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="n8n API key for authentication"
            />
            <p className="text-xs text-gray-500 mt-1">Required if n8n API authentication is enabled</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Webhook Secret (Optional)</label>
            <input aria-label="Webhook Secret (Optional)"
              type="password"
              value={config.webhook_secret}
              onChange={(e) => setConfig({ ...config, webhook_secret: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Secret for webhook validation"
            />
            <p className="text-xs text-gray-500 mt-1">Used to validate webhook requests from n8n</p>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_active"
              checked={config.is_active}
              onChange={(e) => setConfig({ ...config, is_active: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
              Enable n8n integration
            </label>
          </div>
        </div>

        <div className="flex justify-end space-x-4 mt-6">
          <button
            onClick={testConnection}
            disabled={testing}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center space-x-2"
          >
            {testing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                <span>Testing...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Test Connection</span>
              </>
            )}
          </button>
          <button
            onClick={saveConfiguration}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Configuration</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {canOpenN8n ? (
            <a href={config.n8n_url} target="_blank" rel="noopener noreferrer"
              className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center space-x-3">
              <ExternalLink className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium">Open n8n UI</p>
                <p className="text-sm text-gray-500">Access workflow editor</p>
              </div>
            </a>
          ) : (
            <button type="button" disabled className="p-4 border border-gray-200 rounded-lg flex items-center space-x-3 opacity-50 cursor-not-allowed">
              <ExternalLink className="w-5 h-5 text-gray-400" />
              <div className="text-left">
                <p className="font-medium">Open n8n UI</p>
                <p className="text-sm text-gray-500">Configure a browser reachable URL first</p>
              </div>
            </button>
          )}
          <button
            onClick={fetchWorkflows}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center space-x-3 text-left"
          >
            <Settings className="w-5 h-5 text-blue-600" />
            <div>
              <p className="font-medium">Refresh workflows</p>
              <p className="text-sm text-gray-500">Reload saved workflow templates</p>
            </div>
          </button>
        </div>
      </div>

      {/* Available Workflows */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Available Workflows</h2>
        {workflows.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No workflows configured yet. Set up workflows in n8n UI.</p>
        ) : (
          <div className="space-y-3">
            {workflows.map((workflow, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <p className="font-medium">{workflow.name}</p>
                  <p className="text-sm text-gray-500">{workflow.description}</p>
                  <p className="text-xs text-gray-400 mt-1">Webhook: {workflow.webhook_url}</p>
                </div>
                {canOpenN8n && <a href={config.n8n_url} target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1">
                  <span>Configure</span><ExternalLink className="w-3 h-3" />
                </a>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documentation */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">📚 Getting Started</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-blue-800">
          <li>Deploy n8n using: <code className="bg-blue-100 px-2 py-1 rounded">scripts/deploy-n8n.ps1</code> (Windows) or <code className="bg-blue-100 px-2 py-1 rounded">scripts/deploy-n8n.sh</code> (Git Bash)</li>
          <li>Access n8n UI at the configured URL and set up your account</li>
          <li>Import workflow templates from <code className="bg-blue-100 px-2 py-1 rounded">ops/docker/n8n-workflows/</code></li>
          <li>Configure webhook URLs to point to your API: <code className="bg-blue-100 px-2 py-1 rounded">{webhookBase}{'{workflow_name}'}</code></li>
          <li>Set up credentials (WhatsApp, email, etc.) in n8n UI</li>
        </ol>
      </div>
    </div>
  );
};

export default N8NConfiguration;
